import React from 'react';
import {Dialog, DialogActions, DialogContent, DialogTitle, Grid, Divider, useMediaQuery} from '@mui/material';
import {useTheme} from '@mui/material/styles';
import PropTypes from 'prop-types';
import SoftBox from "../../../components/SoftBox";
import SoftTypography from "../../../components/SoftTypography";
import SoftButton from "../../../components/SoftButton";
import {getStatusColor, getStatusDisplayName} from "../../../utils/statusUtils";
import {USER_STATUS_OPTIONS} from "../../../utils/formConstants";
import colors from "../../../assets/theme/base/colors";
import BusinessIcon from '@mui/icons-material/Business';
import DescriptionIcon from '@mui/icons-material/Description';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PhoneIcon from '@mui/icons-material/Phone';
import LanguageIcon from '@mui/icons-material/Language';
import AccountTreeIcon from '@mui/icons-material/AccountTree';

function ViewCompanyDialog({open, onClose, company}) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    if (!company) {
        return null;
    }

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth fullScreen={isMobile}>
            <DialogTitle sx={{borderBottom: `1px solid ${theme.palette.grey[300]}`, bgcolor: 'grey.50', pb: 2}}>
                <SoftBox display="flex" alignItems="center" gap={1.5}>
                    <BusinessIcon sx={{color: colors.info.main, fontSize: 28}} />
                    <SoftTypography variant="h5" fontWeight="bold" color="dark">
                        Company Details
                    </SoftTypography>
                </SoftBox>
            </DialogTitle>
            <DialogContent sx={{p: 3}}>
                <SoftBox>
                    {/* Company Information Section */}
                    <SoftBox mb={3}>
                        <SoftBox display="flex" alignItems="center" gap={1} mb={2}>
                            <BusinessIcon sx={{color: colors.info.main, fontSize: 20}} />
                            <SoftTypography variant="h6" fontWeight="bold" color="dark">
                                Company Information
                            </SoftTypography>
                        </SoftBox>
                        <Divider sx={{mb: 2}} />
                        <Grid container spacing={3}>
                            {company.referenceNumber && (
                                <Grid item xs={12}>
                                    <SoftBox 
                                        p={2} 
                                        sx={{
                                            bgcolor: 'grey.50',
                                            borderRadius: 2,
                                            border: `1px solid ${theme.palette.grey[300]}`
                                        }}
                                    >
                                        <SoftBox display="flex" alignItems="center" gap={1} mb={1}>
                                            <BusinessIcon sx={{color: colors.text.secondary, fontSize: 18}} />
                                            <SoftTypography variant="caption" fontWeight="bold" color="text">
                                                Reference Number
                                            </SoftTypography>
                                        </SoftBox>
                                        <SoftTypography variant="body1" fontWeight="medium" color="dark" sx={{ml: 4}}>
                                            {company.referenceNumber}
                                        </SoftTypography>
                                    </SoftBox>
                                </Grid>
                            )}
                            <Grid item xs={12}>
                                <SoftBox 
                                    p={2} 
                                    sx={{
                                        bgcolor: 'grey.50',
                                        borderRadius: 2,
                                        border: `1px solid ${theme.palette.grey[300]}`
                                    }}
                                >
                                    <SoftBox display="flex" alignItems="center" gap={1} mb={1}>
                                        <BusinessIcon sx={{color: colors.text.secondary, fontSize: 18}} />
                                        <SoftTypography variant="caption" fontWeight="bold" color="text">
                                            Company Name
                                        </SoftTypography>
                                    </SoftBox>
                                    <SoftTypography variant="body1" fontWeight="medium" color="dark" sx={{ml: 4}}>
                                        {company.name || 'Not provided'}
                                    </SoftTypography>
                                </SoftBox>
                            </Grid>

                            {company.description && (
                                <Grid item xs={12}>
                                    <SoftBox 
                                        p={2} 
                                        sx={{
                                            bgcolor: 'grey.50',
                                            borderRadius: 2,
                                            border: `1px solid ${theme.palette.grey[300]}`
                                        }}
                                    >
                                        <SoftBox display="flex" alignItems="flex-start" gap={1} mb={1}>
                                            <DescriptionIcon sx={{color: colors.text.secondary, fontSize: 18, mt: 0.5}} />
                                            <SoftBox flex={1}>
                                                <SoftTypography variant="caption" fontWeight="bold" color="text" mb={1}>
                                                    Description
                                                </SoftTypography>
                                                <SoftTypography variant="body2" color="dark" sx={{whiteSpace: 'pre-wrap', lineHeight: 1.6}}>
                                                    {company.description}
                                                </SoftTypography>
                                            </SoftBox>
                                        </SoftBox>
                                    </SoftBox>
                                </Grid>
                            )}
                        </Grid>
                    </SoftBox>

                    {/* Contact Information Section */}
                    {(company.address || company.phoneNo || company.webUrl) && (
                        <SoftBox mb={3}>
                            <SoftBox display="flex" alignItems="center" gap={1} mb={2}>
                                <LocationOnIcon sx={{color: colors.info.main, fontSize: 20}} />
                                <SoftTypography variant="h6" fontWeight="bold" color="dark">
                                    Contact Information
                                </SoftTypography>
                            </SoftBox>
                            <Divider sx={{mb: 2}} />
                            <Grid container spacing={3}>
                                {company.address && (
                                    <Grid item xs={12}>
                                        <SoftBox 
                                            p={2} 
                                            sx={{
                                                bgcolor: 'grey.50',
                                                borderRadius: 2,
                                                border: `1px solid ${theme.palette.grey[300]}`
                                            }}
                                        >
                                            <SoftBox display="flex" alignItems="flex-start" gap={1} mb={1}>
                                                <LocationOnIcon sx={{color: colors.text.secondary, fontSize: 18, mt: 0.5}} />
                                                <SoftBox flex={1}>
                                                    <SoftTypography variant="caption" fontWeight="bold" color="text" mb={1}>
                                                        Address
                                                    </SoftTypography>
                                                    <SoftTypography variant="body2" color="dark" sx={{whiteSpace: 'pre-wrap', lineHeight: 1.6}}>
                                                        {company.address}
                                                    </SoftTypography>
                                                </SoftBox>
                                            </SoftBox>
                                        </SoftBox>
                                    </Grid>
                                )}

                                {company.phoneNo && (
                                    <Grid item xs={12} sm={6}>
                                        <SoftBox 
                                            p={2} 
                                            sx={{
                                                bgcolor: 'grey.50',
                                                borderRadius: 2,
                                                border: `1px solid ${theme.palette.grey[300]}`
                                            }}
                                        >
                                            <SoftBox display="flex" alignItems="center" gap={1} mb={1}>
                                                <PhoneIcon sx={{color: colors.text.secondary, fontSize: 18}} />
                                                <SoftTypography variant="caption" fontWeight="bold" color="text">
                                                    Phone Number
                                                </SoftTypography>
                                            </SoftBox>
                                            <SoftTypography variant="body2" color="dark" fontWeight="medium" sx={{ml: 4}}>
                                                {company.phoneNo}
                                            </SoftTypography>
                                        </SoftBox>
                                    </Grid>
                                )}

                                {company.webUrl && (
                                    <Grid item xs={12} sm={6}>
                                        <SoftBox 
                                            p={2} 
                                            sx={{
                                                bgcolor: 'grey.50',
                                                borderRadius: 2,
                                                border: `1px solid ${theme.palette.grey[300]}`
                                            }}
                                        >
                                            <SoftBox display="flex" alignItems="center" gap={1} mb={1}>
                                                <LanguageIcon sx={{color: colors.text.secondary, fontSize: 18}} />
                                                <SoftTypography variant="caption" fontWeight="bold" color="text">
                                                    Website URL
                                                </SoftTypography>
                                            </SoftBox>
                                            <SoftTypography variant="body2" sx={{ml: 4}}>
                                                <a 
                                                    href={company.webUrl} 
                                                    target="_blank" 
                                                    rel="noopener noreferrer" 
                                                    style={{
                                                        color: colors.info.main,
                                                        textDecoration: 'none',
                                                        fontWeight: 500
                                                    }}
                                                    onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                                                    onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                                                >
                                                    {company.webUrl}
                                                </a>
                                            </SoftTypography>
                                        </SoftBox>
                                    </Grid>
                                )}
                            </Grid>
                        </SoftBox>
                    )}

                    {/* Configuration Section */}
                    <SoftBox mb={2}>
                        <SoftBox display="flex" alignItems="center" gap={1} mb={2}>
                            <AccountTreeIcon sx={{color: colors.info.main, fontSize: 20}} />
                            <SoftTypography variant="h6" fontWeight="bold" color="dark">
                                Configuration
                            </SoftTypography>
                        </SoftBox>
                        <Divider sx={{mb: 2}} />
                        <Grid container spacing={3}>
                            {company.maxNoOfBranches !== null && company.maxNoOfBranches !== undefined && (
                                <Grid item xs={12} sm={6}>
                                    <SoftBox 
                                        p={2} 
                                        sx={{
                                            bgcolor: 'grey.50',
                                            borderRadius: 2,
                                            border: `1px solid ${theme.palette.grey[300]}`
                                        }}
                                    >
                                        <SoftBox display="flex" alignItems="center" gap={1} mb={1}>
                                            <AccountTreeIcon sx={{color: colors.text.secondary, fontSize: 18}} />
                                            <SoftTypography variant="caption" fontWeight="bold" color="text">
                                                Maximum Number of Branches
                                            </SoftTypography>
                                        </SoftBox>
                                        <SoftBox display="flex" alignItems="center" gap={1} sx={{ml: 4}}>
                                            <SoftTypography variant="h6" fontWeight="bold" color="info">
                                                {company.maxNoOfBranches}
                                            </SoftTypography>
                                            <SoftTypography variant="caption" color="text.secondary">
                                                branches
                                            </SoftTypography>
                                        </SoftBox>
                                    </SoftBox>
                                </Grid>
                            )}

                            <Grid item xs={12} sm={6}>
                                <SoftBox 
                                    p={2} 
                                    sx={{
                                        bgcolor: 'grey.50',
                                        borderRadius: 2,
                                        border: `1px solid ${theme.palette.grey[300]}`
                                    }}
                                >
                                    <SoftBox display="flex" alignItems="center" gap={1} mb={1}>
                                        <SoftTypography variant="caption" fontWeight="bold" color="text">
                                            Status
                                        </SoftTypography>
                                    </SoftBox>
                                    <SoftBox sx={{ml: 4}}>
                                        <SoftButton
                                            variant="gradient"
                                            color={getStatusColor(company.status || USER_STATUS_OPTIONS.ACTIVE)}
                                            size="small"
                                            sx={{minWidth: 70, fontSize: '0.75rem', py: 0.5}}
                                        >
                                            {getStatusDisplayName(company.status || USER_STATUS_OPTIONS.ACTIVE)}
                                        </SoftButton>
                                    </SoftBox>
                                </SoftBox>
                            </Grid>
                        </Grid>
                    </SoftBox>
                </SoftBox>
            </DialogContent>
            <DialogActions sx={{p: 3, borderTop: `1px solid ${theme.palette.grey[300]}`, bgcolor: 'grey.50'}}>
                <SoftButton
                    variant="gradient"
                    color="primary"
                    onClick={onClose}
                    sx={{px: 3, py: 1, borderRadius: 2}}
                >
                    Close
                </SoftButton>
            </DialogActions>
        </Dialog>
    );
}

ViewCompanyDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    company: PropTypes.object,
};

export default ViewCompanyDialog;

